import type { CreditPlanOption, CreditPlanOptionResponse } from '../types';

export const mapCreditPlanOptions = (data: CreditPlanOptionResponse[]): CreditPlanOption[] => {
  const plansByName = groupPlansByName(data);
  const orderedPlanNames = getOrderedPlanNames(plansByName);

  const combinedPlans = combinePlans(plansByName, orderedPlanNames);

  return getPlansWithNextPlanId(combinedPlans);
};

const groupPlansByName = (data: CreditPlanOptionResponse[]): Record<string, CreditPlanOptionResponse[]> => {
  return data.reduce((acc, plan) => {
    if (!acc[plan.name]) {
      acc[plan.name] = [];
    }
    acc[plan.name].push(plan);
    return acc;
  }, {} as Record<string, CreditPlanOptionResponse[]>);
};

const getOrderedPlanNames = (data: Record<string, CreditPlanOptionResponse[]>): string[] => {
  return Object.keys(data).sort((a, b) => {
    return (
      data[a].find((p) => p.subscription_interval === 'month')?.credits_granted -
      data[b].find((p) => p.subscription_interval === 'month')?.credits_granted
    );
  });
};

const combinePlans = (data: Record<string, CreditPlanOptionResponse[]>, orderedPlans: string[]): CreditPlanOption[] => {
  return orderedPlans
    .map((name) => {
      const plans = data[name];
      const monthlyPlan = plans.find((p) => p.subscription_interval === 'month');
      const yearlyPlan = plans.find((p) => p.subscription_interval === 'year');

      if (!monthlyPlan) {
        return null;
      }

      return mapCreditPlanOption(monthlyPlan, yearlyPlan);
    })
    .filter((plan) => plan !== null);
};

const mapCreditPlanOption = (
  monthlyPlan: CreditPlanOptionResponse,
  yearlyPlan?: CreditPlanOptionResponse
): CreditPlanOption => {
  return {
    monthly: {
      id: monthlyPlan.id,
      name: monthlyPlan.name,
      sku: monthlyPlan.sku,
      price: monthlyPlan.price,
      credits_granted: monthlyPlan.credits_granted,
      subscription_interval: monthlyPlan.subscription_interval,
      is_free_trial_enabled: monthlyPlan.is_free_trial_enabled,
      free_trial_interval: monthlyPlan?.free_trial_interval,
      free_trial_interval_count: monthlyPlan?.free_trial_interval_count,
      free_trial_credits_amount: monthlyPlan?.free_trial_credits_amount,
    },
    yearly: {
      id: yearlyPlan?.id,
      name: yearlyPlan?.name,
      sku: yearlyPlan?.sku,
      price: yearlyPlan?.price,
      credits_granted: yearlyPlan?.credits_granted,
      subscription_interval: yearlyPlan?.subscription_interval,
      is_free_trial_enabled: yearlyPlan.is_free_trial_enabled,
      free_trial_interval: yearlyPlan?.free_trial_interval,
      free_trial_interval_count: yearlyPlan?.free_trial_interval_count,
      free_trial_credits_amount: yearlyPlan?.free_trial_credits_amount,
    },
  };
};

const getPlansWithNextPlanId = (plans: CreditPlanOption[]): CreditPlanOption[] => {
  return plans.map((plan, index) => ({
    monthly: {
      ...plan.monthly,
      nextPlanId: index < plans.length - 1 ? plans[index + 1].monthly?.id : null,
    },
    yearly: {
      ...plan.yearly,
      nextPlanId: index < plans.length - 1 ? plans[index + 1].yearly?.id : null,
    },
  }));
};
